/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fromUnixTime, isFuture } from 'date-fns'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import {
  ChannelType,
  EventType,
  eventTypes,
} from '@sportworld/js-sdk/src/types'
import { getContentAccessFlags } from '@sportworld/js-sdk/src/utils'

import { MAX_SAVE_32BIT_INT } from '@/constants/common'
import { ApiService } from '@/service'
import useStore from '@/store/useStore'

export enum VideoOverlayTypes {
  BLOCKED = 'Blocked',
  ENDED = 'Ended',
  COUNTDOWN = 'Countdown',
}

export type VideoOverlay = {
  overlayType?: VideoOverlayTypes
  event?: EventType
}

type VideoOverlayProps = {
  activeChannel?: Nullable<ChannelType>
  isChannelVideoType?: boolean
}

export const useVideoOverlay = ({
  activeChannel,
  isChannelVideoType,
}: VideoOverlayProps) => {
  const { isRegisteredUser } = useAuth()
  const [videoOverlay, setVideoOverlay] = useState<Nullable<VideoOverlay>>()
  const [isChannelWatchStarted, setIsChannelWatchStarted] = useState(false)
  const [eventChannel, setEventChannel] = useState<Nullable<ChannelType>>(null)
  const timerId = useRef<NodeJS.Timeout>()

  const eventFromDetailPage = useStore((state) => state.common.currentEvent)
  const isChannelEventType =
    isChannelVideoType && eventFromDetailPage?.type === eventTypes.CHANNEL

  const channelEventFromDetailPage =
    (isChannelEventType && eventFromDetailPage) || null

  const channelEvent = useStore(
    (state) => state.common.channelEvents[eventChannel?.id || ''],
  )
  const isChannelEvent = !!activeChannel

  const { isAccessible: isAccessibleChannel } = useMemo(() => {
    return getContentAccessFlags(null, eventChannel, isRegisteredUser)
  }, [eventChannel, isRegisteredUser])

  const currentChannelEvent =
    channelEvent === undefined
      ? channelEvent
      : (activeChannel?.watchable && channelEvent) || null

  const event = isChannelEvent
    ? currentChannelEvent
    : channelEventFromDetailPage

  useEffect(() => {
    setIsChannelWatchStarted(false)
    setVideoOverlay(null)
    setEventChannel(activeChannel ?? null)
  }, [activeChannel])

  useEffect(() => {
    if (event === undefined) {
      return
    }
    getOverlayType()
  }, [eventChannel, event])

  const getOverlayType = useCallback(() => {
    timerId.current && clearTimeout(timerId.current)
    const { isAccessible: isAccessibleEvent } = getContentAccessFlags(
      event,
      null,
      isRegisteredUser,
    )
    const isCurrentChannelEvent = !event?.isFutureChannelEvent
    if (!isChannelEvent && event && isAccessibleEvent) {
      const remainingEventTime = event.endTime * 1000 - Date.now()

      timerId.current = setTimeout(
        () => {
          setVideoOverlay({ overlayType: VideoOverlayTypes.ENDED })
        },
        remainingEventTime >= MAX_SAVE_32BIT_INT
          ? MAX_SAVE_32BIT_INT
          : remainingEventTime,
      )
      return
    }
    if (!isChannelEvent && event && !isAccessibleEvent) {
      return setVideoOverlay({ overlayType: VideoOverlayTypes.BLOCKED })
    }

    if (isChannelEvent && !isAccessibleChannel && eventChannel) {
      if (!event) {
        isChannelWatchStarted
          ? setVideoOverlay({ overlayType: VideoOverlayTypes.ENDED })
          : setVideoOverlay({ overlayType: VideoOverlayTypes.BLOCKED })
        return
      }
      const hasAccessibleCurrentEvent =
        isCurrentChannelEvent && isAccessibleEvent
      if (hasAccessibleCurrentEvent) {
        setVideoOverlay(null)
        return setIsChannelWatchStarted(true)
      }
      return checkNextChannelEvent(event, eventChannel.id)
    }

    const hasNonAccessibleCurrentEvent = event && !isAccessibleEvent
    if (isChannelEvent && isAccessibleChannel && hasNonAccessibleCurrentEvent) {
      return setVideoOverlay({ overlayType: VideoOverlayTypes.BLOCKED })
    }
    setVideoOverlay(null)
  }, [event, eventChannel, isChannelWatchStarted])

  const checkNextChannelEvent = useCallback(
    async (event: EventType, channelId: string) => {
      const isFutureChannelEvent = event.isFutureChannelEvent
      let futureChannelEvent
      if (isFutureChannelEvent) {
        futureChannelEvent = event
      } else {
        try {
          const events = await ApiService.getEventsByChannelId(channelId, 2)
          const event = events.items[1]
          const startTime = event.startTime && fromUnixTime(event.startTime)
          if (startTime && isFuture(startTime)) {
            futureChannelEvent = event
          }
        } catch {}
      }
      const { isAccessible: isAccessibleFutureEvent } = getContentAccessFlags(
        futureChannelEvent,
        null,
        isRegisteredUser,
      )

      if (isAccessibleFutureEvent) {
        const remainingEventTime = event.startTime * 1000 - Date.now()
        if (remainingEventTime < 3600 * 1000) {
          return setVideoOverlay({
            overlayType: VideoOverlayTypes.COUNTDOWN,
            event: futureChannelEvent,
          })
        } else {
          const time = remainingEventTime - 3600 * 1000
          timerId.current = setTimeout(
            setVideoOverlay,
            remainingEventTime >= MAX_SAVE_32BIT_INT
              ? MAX_SAVE_32BIT_INT
              : time,
            {
              overlayType: VideoOverlayTypes.COUNTDOWN,
              event: futureChannelEvent,
            },
          )
        }
      }
      isChannelWatchStarted
        ? setVideoOverlay({ overlayType: VideoOverlayTypes.ENDED })
        : setVideoOverlay({ overlayType: VideoOverlayTypes.BLOCKED })
    },
    [isChannelWatchStarted],
  )

  return { videoOverlay, setVideoOverlay }
}
